import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useLiveQuery } from 'dexie-react-hooks';
import {
  formatCents,
  newId,
  type CompareResponse,
  type CompareResultDto,
  type CreateVariantResponse,
} from '@familyhub/shared';
import { api, isOffline } from '../api/client';
import { db, storesByName} from '../db';
import { addItem, moveToStore } from '../list/actions';
import { sync } from '../sync/engine';
import { ProductThumb } from '../components/ProductThumb';
import { OrganicLeaf } from '../components/OrganicLeaf';
import { shopProblem } from '../list/shops';

/**
 * The comparison view (ARCHITECTURE §5.5).
 *
 * Shelf price large, unit price small underneath: you pay the shelf price but
 * compare on the unit price, so both are shown. The habitual choice pins to the
 * top with its reason, because you are comparing against your default rather
 * than shopping blind.
 *
 * **It presents facts and marks the habitual choice. It never says which to
 * buy.** Moving the item to a cheaper shop is a thing the user does, not a
 * thing the app suggests.
 */
export function CompareScreen() {
  const { genericId } = useParams<{ genericId: string }>();
  const [data, setData] = useState<CompareResponse | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  /** When the answer on screen was fetched, if it came from the cache. */
  const [cachedAt, setCachedAt] = useState<string | null>(null);

  const stores = useLiveQuery(storesByName, [], []);
  const listItem = useLiveQuery(
    async () =>
      genericId
        ? (await db.list_items.where('generic_product_id').equals(genericId).toArray()).find(
            (i) => !i.deleted_at,
          )
        : undefined,
    [genericId],
  );
  const generic = useLiveQuery(
    () => (genericId ? db.generic_products.get(genericId) : undefined),
    [genericId],
  );

  useEffect(() => {
    if (!genericId) return;
    let cancelled = false;
    void (async () => {
      try {
        const response = await api<CompareResponse>(`/api/v1/compare?generic_id=${genericId}`);
        if (cancelled) return;
        setData(response);
        setCachedAt(null);
        // Kept for the next time this is opened without reception.
        await db.compare_cache.put({
          generic_product_id: genericId,
          response,
          fetched_at: new Date().toISOString(),
        });
      } catch (err) {
        if (cancelled) return;
        // Comparing is an online question — but it is asked in a shop. The last
        // answer, dated, beats no answer at all; what it must never do is
        // pretend to be current.
        const cached = await db.compare_cache.get(genericId);
        if (cached) {
          setData(cached.response as CompareResponse);
          setCachedAt(cached.fetched_at);
        } else {
          setProblem(
            isOffline(err)
              ? 'Prijzen vergelijken kan niet zonder verbinding, en hiervoor is nog niets bewaard.'
              : 'De prijzen konden even niet vergeleken worden.',
          );
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [genericId]);

  const storeName = (code: string) => stores.find((s) => s.code === code)?.name ?? code;
  const keyOf = (r: CompareResultDto) =>
    `${r.variant.store}-${r.variant.external_id ?? r.variant.id}`;

  const offline = typeof navigator !== 'undefined' && navigator.onLine === false;

  /**
   * Whether acting on this row will get the full answer.
   *
   * A row the household already owns is a local move and always works. One it
   * does not own has to be selected first — the call that fetches the EAN and
   * records a first price — and that needs the network. The move still happens
   * either way, but it lands generic-only, with no price beside it.
   *
   * Saying so **before** the tap matters here more than anywhere: this screen is
   * read standing in the aisle of the shop it just recommended, and finding out
   * afterwards is the worst version of that.
   */
  const degraded = (result: CompareResultDto) => offline && result.variant.id === null;

  /**
   * Act on a row: move the list item to this shop's product, or add it if the
   * product is not on the list yet.
   *
   * A row the household has never bought (`in_catalog: false`) has no variant
   * to point at, so selection runs first — the same `POST /variants` the add
   * screen uses, which is what fetches the EAN and records a first price. If
   * that fails the move still happens, just generic-only: better a correct
   * store with no price than a wrong price.
   */
  async function choose(result: CompareResultDto) {
    if (!genericId) return;
    const store = stores.find((s) => s.code === result.variant.store);
    if (!store) return;

    setBusy(keyOf(result));
    setProblem(null);
    let variantId = result.variant.id;

    if (!variantId && result.variant.external_id) {
      try {
        const created = await api<CreateVariantResponse>('/api/v1/variants', {
          method: 'POST',
          body: {
            id: newId(),
            store: result.variant.store,
            external_id: result.variant.external_id,
            // Attach to the generic being compared rather than letting
            // selection derive a new one — the user has just told us these are
            // the same product, and that is a human classification.
            generic_product_id: genericId,
            name: result.variant.name,
            brand: result.variant.brand,
            price_cents: result.shelf_price_cents,
          },
        });
        variantId = created.variant.id;
      } catch {
        setProblem('Dat product kon niet toegevoegd worden, dus het is zonder verplaatst.');
      }
    }

    if (listItem) await moveToStore(listItem.id, store.id, variantId);
    else await addItem({ genericProductId: genericId, variantId, storeId: store.id });

    void sync();
    setBusy(null);
  }

  return (
    <>
      <header className="topbar">
        <Link className="back btn btn-quiet" to="/boodschappen">
          ‹ Terug
        </Link>
        <h1>{data?.generic_product?.name ?? generic?.name ?? 'Vergelijken'}</h1>
      </header>

      <main className="stack">
        {problem && <p className="banner">{problem}</p>}

        {/* Never present an old price as current (ADAPTERS §5.2). The whole
            screen is dated, not just the rows. */}
        {cachedAt && (
          <p className="banner">
            Bewaarde prijzen van {new Date(cachedAt).toLocaleDateString('nl-NL')} — nu niet opnieuw gecontroleerd.
          </p>
        )}

        {offline && data?.results.some((r) => r.variant.id === null) && (
          <p className="row-sub">
            Producten die je nog nooit gekocht hebt, hebben verbinding nodig om goed toegevoegd te
            worden. Je kunt het nu wel naar die winkel verplaatsen — er staat dan alleen geen prijs
            bij tot je weer online bent.
          </p>
        )}
        {!data && !problem && <p className="empty">Winkels raadplegen…</p>}

        {data?.results.map((result) => (
          <div
            key={`${result.variant.store}-${result.variant.external_id ?? result.variant.id}`}
            className={result.habitual ? 'result habitual' : 'result'}
          >
            {/* The same organic leaf the list and the search rows carry, so a
                biologisch product is recognisable at a glance on the one screen
                whose whole job is telling near-identical products apart. */}
            <span className="thumb-wrap">
              {/*
               * Larger here than on the list. This screen exists to answer "is
               * the cheap one the same product", and four shops' names for one
               * yoghurt differ enough that the packshot answers it faster than
               * the name does.
               */}
              <ProductThumb src={result.variant.image_path} name={result.variant.name} size="compare" />
              {result.variant.organic && <OrganicLeaf size="thumb" />}
            </span>
            <span className="row-main">
              <span className="row-name">{result.variant.name}</span>
              <span className="row-sub">
                {[
                  storeName(result.variant.store),
                  result.variant.size_label,
                  result.variant.organic ? 'biologisch' : null,
                  result.reason,
                  result.stale || cachedAt ? 'oude prijs' : null,
                  result.promo_label,
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </span>
            </span>

            <span className={result.stale || cachedAt ? 'price stale' : 'price'}>
              {result.price_cents === null ? '—' : formatCents(result.price_cents)}
              {/* Omitted rather than guessed when the size is unknown. */}
              {result.unit_price_cents !== null && (
                <small>
                  {formatCents(result.unit_price_cents)}/{result.unit}
                </small>
              )}
            </span>

            <button
              className="btn btn-quiet"
              disabled={busy !== null}
              onClick={() => void choose(result)}
              // The label alone cannot carry the caveat, so the accessible name
              // does — a screen-reader user gets the same warning.
              aria-label={
                degraded(result)
                  ? `${listItem ? 'Verplaatsen naar' : 'Toevoegen van'} ${storeName(result.variant.store)}, zonder prijsgegevens`
                  : undefined
              }
            >
              {busy === keyOf(result)
                ? '…'
                : degraded(result)
                  ? listItem
                    ? 'Verplaatsen (geen gegevens)'
                    : 'Toevoegen (geen gegevens)'
                  : listItem
                    ? 'Hierheen'
                    : 'Toevoegen'}
            </button>
          </div>
        ))}

        {data && data.results.length === 0 && (
          <p className="empty">Niets te vergelijken — geen enkele winkel gaf een match.</p>
        )}

        {/* Unavailable stores are listed rather than omitted, so their absence
            is never mistaken for "they don't sell it". */}
        {data &&
          Object.entries(data.stores)
            .filter(([, status]) => !status.ok)
            .map(([code, status]) => (
              <p key={code} className="row-sub" style={{ padding: '0 4px' }}>
                {storeName(code)} {shopProblem(status.reason)}
              </p>
            ))}
      </main>
    </>
  );
}
